import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Key, Files, Activity } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { useNavigate } from 'react-router-dom'
import type { FileMetadata } from '@openartifacts/types'

interface DashboardStats {
  filesCount: number
  storageUsed: number
  apiKeysCount: number
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export function DashboardPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats>({
    filesCount: 0,
    storageUsed: 0,
    apiKeysCount: 0,
  })
  const [files, setFiles] = useState<FileMetadata[]>([])
  const [filesLoading, setFilesLoading] = useState(true)

  const loadDashboardStats = async () => {
    try {
      // Load files with full metadata for both stats and files list
      const { data: filesData, error: filesError } = await supabase
        .from('files')
        .select('id, path, size_bytes, content_type, current_version, created_at, updated_at')
        .order('updated_at', { ascending: false })

      if (filesError) {
        console.error('Error loading files:', filesError)
      } else {
        const filesCount = filesData?.length || 0
        const storageUsed = filesData?.reduce((sum, file) => sum + (file.size_bytes || 0), 0) || 0

        setStats(prev => ({
          ...prev,
          filesCount,
          storageUsed,
        }))

        // Transform files data to match FileMetadata type
        const transformedFiles: FileMetadata[] = (filesData || []).map(file => ({
          id: file.id,
          path: file.path,
          size: file.size_bytes || 0,
          mimeType: file.content_type || 'application/octet-stream',
          version: file.current_version || 1,
          createdAt: file.created_at,
          updatedAt: file.updated_at,
        }))

        setFiles(transformedFiles)
      }

      // Load API keys count
      const { data: apiKeysData, error: apiKeysError } = await supabase
        .from('api_keys')
        .select('id')
        .eq('is_active', true)

      if (apiKeysError) {
        console.error('Error loading API keys:', apiKeysError)
      } else {
        setStats(prev => ({
          ...prev,
          apiKeysCount: apiKeysData?.length || 0,
        }))
      }
    } catch (err) {
      console.error('Error loading dashboard stats:', err)
      setError(err instanceof Error ? err.message : 'Failed to load dashboard statistics')
    } finally {
      setFilesLoading(false)
    }
  }

  useEffect(() => {
    console.log('DashboardPage mounted')
    console.log('User data:', user)

    const initializeDashboard = async () => {
      try {
        await loadDashboardStats()
        setLoading(false)
        console.log('Dashboard initialization complete')
      } catch (err) {
        console.error('Error initializing dashboard:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        setLoading(false)
      }
    }

    initializeDashboard()
  }, [user])

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <LoadingSpinner size='lg' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='space-y-6'>
        <h1 className='text-2xl font-semibold'>Dashboard Error</h1>
        <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-4'>
          <p className='text-destructive'>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Hero Section - Most Important */}
      <div className='space-y-2'>
        <h1 className='text-2xl font-semibold'>
          Welcome back, {user?.user_metadata?.user_name || 'Developer'}
        </h1>
      </div>

      {/* Primary Action - Show only if user has no API keys */}
      {stats.apiKeysCount === 0 && (
        <Card className='border-2 border-dashed border-primary/20 bg-primary/5'>
          <CardContent className='p-4'>
            <div className='flex items-center space-x-4'>
              <div className='w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0'>
                <Key className='w-5 h-5 text-primary' />
              </div>
              <div className='min-w-0 flex-1'>
                <h3 className='text-base font-medium'>Generate Your First API Key</h3>
                <p className='text-sm text-muted-foreground'>
                  You'll need an API key to start using the OpenArtifacts SDK
                </p>
              </div>
              <Button onClick={() => navigate('/api-keys')} className='flex-shrink-0'>
                <Key className='w-4 h-4 mr-2' />
                Generate Key
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Essential Stats - Key metrics only */}
      <div className='grid gap-4 sm:grid-cols-3'>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0'>
                <Files className='h-5 w-5 text-primary' />
              </div>
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-medium text-muted-foreground'>Files</p>
                <p className='text-2xl font-bold'>{stats.filesCount.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-secondary/50 rounded-lg flex items-center justify-center flex-shrink-0'>
                <Activity className='h-5 w-5 text-secondary-foreground' />
              </div>
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-medium text-muted-foreground'>Storage</p>
                <p className='text-2xl font-bold'>{formatBytes(stats.storageUsed)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-accent/50 rounded-lg flex items-center justify-center flex-shrink-0'>
                <Key className='h-5 w-5 text-accent-foreground' />
              </div>
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-medium text-muted-foreground'>API Keys</p>
                <p className='text-2xl font-bold'>{stats.apiKeysCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Files Section */}
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Recent Files</CardTitle>
        </CardHeader>
        <CardContent>
          {filesLoading ? (
            <div className='flex items-center justify-center py-8'>
              <LoadingSpinner size='md' />
            </div>
          ) : files.length === 0 ? (
            <div className='text-center py-8'>
              <Files className='w-12 h-12 text-muted-foreground mx-auto mb-4' />
              <h3 className='font-medium text-lg mb-2'>No files yet</h3>
              <p className='text-sm text-muted-foreground'>
                Files created via the OpenArtifacts SDK will appear here
              </p>
            </div>
          ) : (
            <div className='space-y-3'>
              {files.slice(0, 5).map(file => (
                <div
                  key={file.id}
                  className='flex items-center justify-between p-3 rounded-lg border'
                >
                  <div className='flex items-center space-x-3'>
                    <div className='w-8 h-8 bg-muted rounded flex items-center justify-center'>
                      <Files className='w-4 h-4 text-muted-foreground' />
                    </div>
                    <div>
                      <p className='font-medium text-sm'>
                        {file.path.split('/').pop() || file.path}
                      </p>
                      <p className='text-xs text-muted-foreground'>{file.path}</p>
                    </div>
                  </div>
                  <div className='text-right'>
                    <p className='text-sm font-medium'>{formatBytes(file.size)}</p>
                    <p className='text-xs text-muted-foreground'>
                      {new Date(file.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
              {files.length > 5 && (
                <div className='text-center pt-2'>
                  <p className='text-sm text-muted-foreground'>Showing 5 of {files.length} files</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
